import React, { useState, useMemo } from "react";
import { PlusIcon, EditIcon, TrashIcon, CheckIcon, XIcon } from "../icons";
import S from "../styles";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

function CellInput({ value, onChange, placeholder, type = "text", style: s }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ ...S.cellInput, ...s }}
    />
  );
}

function SortArrow({ sortCol, col, sortDir }) {
  if (sortCol !== col) return <span style={{ opacity: 0.3, marginLeft: 4 }}>&#8597;</span>;
  return <span style={{ marginLeft: 4, color: "#0033FF" }}>{sortDir === "asc" ? "\u2191" : "\u2193"}</span>;
}

export default function DatabasePage({ materials, setMaterials }) {
  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState({});
  const [addingNew, setAddingNew] = useState(false);
  const [newRow, setNewRow] = useState({ type: "", color: "", sheetSize: "", thickness: "", price: "" });
  const [filter, setFilter] = useState("");
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const filtered = useMemo(() => {
    let l = materials;
    if (filter) {
      const f = filter.toLowerCase();
      l = l.filter(
        (m) =>
          m.type.toLowerCase().includes(f) ||
          m.color.toLowerCase().includes(f) ||
          m.sheetSize.toLowerCase().includes(f) ||
          m.thickness.toLowerCase().includes(f)
      );
    }
    if (sortCol) {
      l = [...l].sort((a, b) => {
        let va = a[sortCol], vb = b[sortCol];
        if (sortCol === "price") return sortDir === "asc" ? va - vb : vb - va;
        va = String(va).toLowerCase();
        vb = String(vb).toLowerCase();
        return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      });
    }
    return l;
  }, [materials, filter, sortCol, sortDir]);

  const handleSort = (c) => {
    if (sortCol === c) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortCol(c); setSortDir("asc"); }
  };

  const saveEdit = () => {
    if (!editRow.type || !editRow.color || !editRow.sheetSize || !editRow.thickness || editRow.price === "") return;
    const updated = { ...editRow, price: parseFloat(editRow.price) || 0 };
    setMaterials(materials.map((m) => (m.id === editId ? updated : m)));
    fetch(`${API_URL}/api/materials/${editId}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(updated) }).catch(() => {});
    setEditId(null);
  };

  const deleteRow = (id) => {
    setMaterials(materials.filter((m) => m.id !== id));
    fetch(`${API_URL}/api/materials/${id}`, { method: "DELETE" }).catch(() => {});
  };

  const addRow = () => {
    if (!newRow.type || !newRow.color || !newRow.sheetSize || !newRow.thickness || newRow.price === "") return;
    const data = { ...newRow, price: parseFloat(newRow.price) || 0 };
    fetch(`${API_URL}/api/materials`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) })
      .then((r) => r.json()).then((created) => { if (created.id) setMaterials([...materials, created]); }).catch(() => {});
    setAddingNew(false);
    setNewRow({ type: "", color: "", sheetSize: "", thickness: "", price: "" });
  };

  const cols = [
    { key: "type", label: "Type", width: "16%" },
    { key: "color", label: "Color / Variant", width: "22%" },
    { key: "sheetSize", label: "Sheet Size", width: "16%" },
    { key: "thickness", label: "Thickness", width: "14%" },
    { key: "price", label: "Price", width: "14%" },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter materials..." style={{ ...S.input, marginBottom: 0, maxWidth: 280 }} />
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <span style={{ color: "rgba(10,10,15,0.58)", fontSize: 15 }}>{materials.length} material{materials.length !== 1 ? "s" : ""}</span>
          {!addingNew && <button onClick={() => setAddingNew(true)} style={S.addBtn}><PlusIcon /> Add Acrylic</button>}
        </div>
      </div>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              {cols.map((c) => (
                <th key={c.key} onClick={() => handleSort(c.key)} style={{ ...S.th, width: c.width, cursor: "pointer", userSelect: "none" }}>
                  {c.label}<SortArrow sortCol={sortCol} col={c.key} sortDir={sortDir} />
                </th>
              ))}
              <th style={{ ...S.th, width: "18%", textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {addingNew && (
              <tr style={S.addingRow}>
                <td style={S.td}><CellInput value={newRow.type} onChange={(v) => setNewRow({ ...newRow, type: v })} placeholder="e.g. Acrylic" /></td>
                <td style={S.td}><CellInput value={newRow.color} onChange={(v) => setNewRow({ ...newRow, color: v })} placeholder="e.g. Frosted Glass" /></td>
                <td style={S.td}><CellInput value={newRow.sheetSize} onChange={(v) => setNewRow({ ...newRow, sheetSize: v })} placeholder='e.g. 12x20"' /></td>
                <td style={S.td}><CellInput value={newRow.thickness} onChange={(v) => setNewRow({ ...newRow, thickness: v })} placeholder='e.g. 1/4"' /></td>
                <td style={S.td}><CellInput value={newRow.price} onChange={(v) => setNewRow({ ...newRow, price: v })} placeholder="0.00" type="number" style={{ textAlign: "right" }} /></td>
                <td style={{ ...S.td, textAlign: "right" }}>
                  <button onClick={addRow} style={S.iconBtn} title="Save"><CheckIcon /></button>
                  <button onClick={() => { setAddingNew(false); setNewRow({ type: "", color: "", sheetSize: "", thickness: "", price: "" }); }} style={{ ...S.iconBtn, marginLeft: 6 }} title="Cancel"><XIcon /></button>
                </td>
              </tr>
            )}
            {filtered.map((m) => (
              <tr key={m.id} style={editId === m.id ? S.editingRow : {}}>
                {editId === m.id ? (
                  <>
                    <td style={S.td}><CellInput value={editRow.type} onChange={(v) => setEditRow({ ...editRow, type: v })} /></td>
                    <td style={S.td}><CellInput value={editRow.color} onChange={(v) => setEditRow({ ...editRow, color: v })} /></td>
                    <td style={S.td}><CellInput value={editRow.sheetSize} onChange={(v) => setEditRow({ ...editRow, sheetSize: v })} /></td>
                    <td style={S.td}><CellInput value={editRow.thickness} onChange={(v) => setEditRow({ ...editRow, thickness: v })} /></td>
                    <td style={S.td}><CellInput value={editRow.price} onChange={(v) => setEditRow({ ...editRow, price: v })} type="number" style={{ textAlign: "right" }} /></td>
                    <td style={{ ...S.td, textAlign: "right" }}>
                      <button onClick={saveEdit} style={S.iconBtn} title="Save"><CheckIcon /></button>
                      <button onClick={() => setEditId(null)} style={{ ...S.iconBtn, marginLeft: 6 }} title="Cancel"><XIcon /></button>
                    </td>
                  </>
                ) : (
                  <>
                    <td style={S.td}><span style={S.typeBadge}>{m.type}</span></td>
                    <td style={S.td}>{m.color}</td>
                    <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 15 }}>{m.sheetSize}</td>
                    <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 15 }}>{m.thickness}</td>
                    <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 15, textAlign: "right", color: "#0033FF" }}>${m.price.toFixed(2)}</td>
                    <td style={{ ...S.td, textAlign: "right" }}>
                      <button onClick={() => { setEditId(m.id); setEditRow({ ...m }); }} style={S.iconBtn} title="Edit"><EditIcon /></button>
                      <button onClick={() => deleteRow(m.id)} style={{ ...S.iconBtn, marginLeft: 6 }} title="Delete"><TrashIcon /></button>
                    </td>
                  </>
                )}
              </tr>
            ))}
            {filtered.length === 0 && !addingNew && (
              <tr>
                <td colSpan={6} style={{ ...S.td, textAlign: "center", color: "rgba(10,10,15,0.58)", padding: 40 }}>
                  {filter ? "No materials match." : "No materials yet."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
